// People on Banger (wave 13, pillar 2; social, docs/backend/social.md "People"): posts, profile cards, the feed and
// friends' "playing now". The visibility rules are social's (minors' posts reach friends and mutual follows only);
// the site just shows what it's given. Every post shows Report; the post's game, asset or event shows as a card you
// can play or open.

import { api, ApiError } from "./api.ts";
import { gameById, getCatalogue } from "./catalogue.ts";
import { clear, h, link, toast } from "./dom.ts";
import { downloadPath, launch } from "./play.ts";
import { navigate } from "./router.ts";
import { coverUrl } from "./rows.ts";
import { currentMe, signedIn } from "./session.ts";

export type EmbedKind = "game" | "asset" | "event";

export interface Post {
  id: string;
  author: { id: string; name: string | null; picture?: string | null };
  kind: "post" | "release";
  /** posted as a studio the author claimed (BANGERS.md slice 1) */
  studio?: { id: string; name: string | null } | null;
  body: string;
  embed: { kind: EmbedKind; id: string } | null;
  media: { kind: "clip" | "shot"; id: string }[];
  version: string | null;
  reply_to: string | null;
  replies: number;
  /** hearts, and whether you gave one (BANGERS.md slice 2) */
  hearts: number;
  hearted: boolean;
  /** reposts (BANGERS.md slice 5): adults' public bangers only */
  reposts?: number;
  reposted?: boolean;
  can_repost?: boolean;
  /** in a feed: someone you follow reposted it */
  reposted_by?: { id: string; name: string | null };
  feed_at?: number;
  status?: "draft" | "visible" | "hidden" | "removed";
  created_at: number;
  published_at: number | null;
  can_reply: boolean;
}

export interface Playing {
  user_id: string;
  name: string | null;
  game_id: string;
  joinable: boolean;
  party_id: string | null;
}

export interface Link {
  label: string;
  url: string;
}

export interface Card {
  user_id: string;
  bio: string | null;
  banner: string | null;
  links: Link[];
  pinned: { kind: "game" | "asset"; id: string }[];
  building_now: string | null;
  updated_at: number | null;
  visible: boolean;
  /** their avatar portrait, when their profile shows it to you */
  picture?: string | null;
}

export type FeedTab = "following" | "friends" | "games";

export interface FeedPage {
  tab: FeedTab;
  posts: Post[];
  playing: Playing[];
  next: number | null;
}

let styled = false;
/** The people pages' styles (public/people.css), once. */
export function peopleStyles(): void {
  if (styled) return;
  styled = true;
  document.head.append(h("link", { rel: "stylesheet", href: "/people.css" }));
}

/** "3 min", "2 h", "5 d", then the date. */
export function ago(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return "now";
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${Math.round(s / 3600)} h`;
  if (s < 7 * 86400) return `${Math.round(s / 86400)} d`;
  return new Date(ms).toISOString().slice(0, 10);
}

export const initial = (name: string | null) => (name ?? "?").replace("@", "").slice(0, 1).toUpperCase() || "?";

/** The round face: the avatar portrait when there is one (BANGERS.md slice 4), else the initial. */
export function avatar(name: string | null, size: "small" | "" = "", picture: string | null = null): HTMLElement {
  const el = h("span", { class: `avatar-badge ${size}${picture ? " has-picture" : ""}`.trim(), "aria-hidden": "true" }, picture ? null : initial(name));
  if (picture) el.style.backgroundImage = `url("${encodeURI(picture)}")`;
  return el;
}

/** Plain-language errors for the post and profile calls. */
export function peopleError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "filtered") return "That can't go up. Try different words.";
    if (e.code === "rate_limited") return "That's a lot of bangers. Take a breather and try again in a bit.";
    if (e.code === "not_allowed") return e.message || "You can't do that here.";
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}

// ---- embeds ----

/** The post's game as a playable card (cover, title, Play in the Banger app). */
function gameEmbed(id: string): HTMLElement {
  const card = h("div", { class: "embed embed-game" }, h("div", { class: "embed-art" }), h("div", { class: "embed-body" }, h("strong", null, "A game on Banger")));
  void getCatalogue()
    .then((cat) => {
      const g = gameById(cat, id);
      if (!g) return;
      const art = coverUrl(g);
      clear(
        card,
        link(`/g/${g.id}`, { class: "embed-art", style: art ? { backgroundImage: `url("${art}")` } : {} }),
        h(
          "div",
          { class: "embed-body" },
          link(`/g/${g.id}`, { class: "embed-title" }, g.title),
          h("span", { class: "dim small" }, g.creator?.name ? `by ${g.creator.name}` : "Game"),
        ),
        h(
          "button",
          {
            type: "button",
            class: "btn small play",
            "aria-label": `Play ${g.title} in the Banger app`,
            onclick: () => launch(g.id, { onMissing: () => navigate(downloadPath(g.id)) }),
          },
          "Play",
        ),
      );
    })
    .catch(() => {});
  return card;
}

export function embedCard(e: Post["embed"]): HTMLElement | null {
  if (!e) return null;
  if (e.kind === "game") return gameEmbed(e.id);
  if (e.kind === "asset") {
    const [creator, name] = e.id.split("/");
    return link(`/store/a/${creator}/${name}`, { class: "embed embed-asset" }, h("div", { class: "embed-body" }, h("span", { class: "embed-title" }, name), h("span", { class: "dim small" }, `Asset by @${creator} · open in the Workshop`)));
  }
  return link(`/events#${encodeURIComponent(e.id)}`, { class: "embed embed-event" }, h("div", { class: "embed-body" }, h("span", { class: "embed-title" }, "An event on Banger"), h("span", { class: "dim small" }, "See it on Events")));
}

function mediaStrip(m: Post["media"]): HTMLElement | null {
  const clips = m.filter((x) => x.kind === "clip");
  if (!clips.length) return null;
  return h("div", { class: "post-media" }, clips.map((c) => link(`/clip/${encodeURIComponent(c.id)}`, { class: "media-chip" }, "▶ Watch the clip")));
}

// ---- report ----

const REASONS: [string, string][] = [
  ["spam", "Spam"],
  ["harassment", "Harassment or bullying"],
  ["hate", "Hate"],
  ["sexual", "Sexual content"],
  ["self_harm", "Self-harm"],
  ["other", "Something else"],
];

function reportForm(p: Post, host: HTMLElement, back: () => void): HTMLElement {
  const reason = h("select", { name: "reason", "aria-label": "Reason" }, REASONS.map(([v, t]) => h("option", { value: v }, t)));
  const note = h("input", { name: "note", maxlength: 500, placeholder: "Anything moderators should know (optional)", "aria-label": "Note" });
  const msg = h("div", { "aria-live": "polite" });
  return h(
    "form",
    {
      class: "report-form",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        try {
          const n = (note as HTMLInputElement).value.trim();
          await api("social", "/reports", { body: { user_id: p.author.id, post_id: p.id, reason: (reason as HTMLSelectElement).value, ...(n ? { note: n } : {}) } });
          clear(host, h("p", { class: "small dim", role: "status" }, "Thanks. Moderators will look at it. You can also block this person from their profile."));
        } catch (x) {
          clear(msg, h("p", { class: "error small", role: "alert" }, peopleError(x)));
        }
      },
    },
    reason,
    note,
    h("div", { class: "actions" }, h("button", { class: "btn small", type: "submit" }, "Send report"), h("button", { class: "btn small ghost", type: "button", onclick: back }, "Cancel")),
    msg,
  );
}

// ---- a post ----

export interface PostOpts {
  /** link the time to the post's own page (off on that page) */
  permalink?: boolean;
  onDelete?: () => void;
}

export function postCard(p: Post, opts: PostOpts = { permalink: true }): HTMLElement {
  const me = currentMe();
  const mine = me?.id === p.author.id;
  const name = p.author.name ?? "Player";
  const tools = h("div", { class: "post-tools" });
  // status is only on your own bangers; anyone else's that you can see is live
  const live = (p.status ?? "visible") === "visible";
  const extra = h("div", { class: "post-extra" });
  const resetTools = () => {
    clear(
      tools,
      live ? heartButton(p) : null,
      live && (p.can_repost || p.reposted) ? repostButton(p) : null,
      p.can_reply && opts.permalink ? link(`/p/${p.id}`, { class: "tool" }, p.replies ? `Reply · ${p.replies}` : "Reply") : p.replies && opts.permalink ? link(`/p/${p.id}`, { class: "tool" }, `${p.replies} ${p.replies === 1 ? "reply" : "replies"}`) : null,
      mine
        ? h(
            "button",
            {
              type: "button",
              class: "tool",
              onclick: async () => {
                if (!confirm("Delete this banger?")) return;
                try {
                  await api("social", `/posts/${p.id}`, { method: "DELETE" });
                  toast("Banger deleted.");
                  opts.onDelete?.();
                } catch (e) {
                  toast(peopleError(e));
                }
              },
            },
            "Delete",
          )
        : h(
            "button",
            {
              type: "button",
              class: "tool",
              onclick: () => {
                if (!signedIn()) return navigate(`/signin?next=${encodeURIComponent(location.pathname)}`);
                clear(extra, reportForm(p, extra, () => clear(extra)));
              },
            },
            "Report",
          ),
    );
  };
  resetTools();
  const when = p.published_at ?? p.created_at;
  const time = opts.permalink ? link(`/p/${p.id}`, { class: "post-time dim small" }, ago(when)) : h("span", { class: "post-time dim small" }, ago(when));
  return h(
    "article",
    { class: `post${p.kind === "release" ? " post-release" : ""}${p.reply_to ? " post-reply" : ""}`, "data-post": p.id },
    avatar(name, "", p.author.picture ?? null),
    h(
      "div",
      { class: "post-main" },
      h(
        "header",
        { class: "post-head" },
        p.reposted_by ? h("span", { class: "post-reposted dim small" }, `\u21bb Reposted by ${p.reposted_by.id === me?.id ? "you" : (p.reposted_by.name ?? "someone you follow")}`) : null,
        p.studio ? link(`/c/${encodeURIComponent(p.studio.id)}`, { class: "post-name post-studio" }, p.studio.name ?? "Studio") : h("strong", { class: "post-name" }, name),
        p.studio ? h("span", { class: "dim small" }, `by ${name}`) : null,
        p.kind === "release" ? h("span", { class: "chip" }, p.version ? `Release ${p.version}` : "Release") : null,
        p.status === "hidden" ? h("span", { class: "chip warn" }, "Hidden by moderators") : null,
        p.status === "draft" ? h("span", { class: "chip" }, "Draft") : null,
        time,
      ),
      h("p", { class: "post-body" }, withMentions(p.body)),
      mediaStrip(p.media),
      embedCard(p.embed),
      tools,
      extra,
    ),
  );
}

/** The heart: one per person, the same gesture as hearting a game. Signed out, it goes to sign in. */
export function heartButton(p: Pick<Post, "id" | "hearts" | "hearted">): HTMLElement {
  let { hearts, hearted } = p;
  const label = () => (hearts ? `\u2665 ${hearts}` : "\u2665");
  const btn = h("button", { type: "button", class: `tool heart${hearted ? " on" : ""}`, "aria-pressed": String(hearted), "aria-label": hearted ? "Remove your heart" : "Heart this banger" }, label());
  btn.onclick = async () => {
    if (!signedIn()) return navigate(`/signin?next=${encodeURIComponent(location.pathname)}`);
    const want = !hearted;
    try {
      const r = await api<{ hearts: number; hearted: boolean }>("social", `/posts/${p.id}/heart`, { method: want ? "PUT" : "DELETE" });
      ({ hearts, hearted } = r);
    } catch (e) {
      return toast(peopleError(e));
    }
    btn.textContent = label();
    btn.classList.toggle("on", hearted);
    btn.setAttribute("aria-pressed", String(hearted));
    btn.setAttribute("aria-label", hearted ? "Remove your heart" : "Heart this banger");
  };
  return btn;
}

/** A repost: shares an adult's public banger with the people who follow you. */
export function repostButton(p: Pick<Post, "id" | "reposts" | "reposted">): HTMLElement {
  let reposts = p.reposts ?? 0;
  let reposted = !!p.reposted;
  const label = () => (reposts ? `\u21bb ${reposts}` : "\u21bb Repost");
  const btn = h("button", { type: "button", class: `tool repost${reposted ? " on" : ""}`, "aria-pressed": String(reposted) }, label());
  btn.onclick = async () => {
    if (!signedIn()) return navigate(`/signin?next=${encodeURIComponent(location.pathname)}`);
    try {
      ({ reposts, reposted } = await api<{ reposts: number; reposted: boolean }>("social", `/posts/${p.id}/repost`, { method: reposted ? "DELETE" : "PUT" }));
    } catch (e) {
      return toast(peopleError(e));
    }
    btn.textContent = label();
    btn.classList.toggle("on", reposted);
    btn.setAttribute("aria-pressed", String(reposted));
    if (reposted) toast("Reposted to the people who follow you.");
  };
  return btn;
}

/** A banger's text with each @handle linked to that profile (social tells the players it names). */
export function withMentions(body: string): (string | HTMLElement)[] {
  const out: (string | HTMLElement)[] = [];
  let last = 0;
  for (const m of body.matchAll(/(^|[^\w@/])@([A-Za-z0-9_]{2,30})\b/g)) {
    const at = m.index! + m[1].length;
    if (at > last) out.push(body.slice(last, at));
    out.push(link(`/u/${encodeURIComponent(m[2])}`, { class: "mention" }, `@${m[2]}`));
    last = at + 1 + m[2].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}

// ---- the composer ----

export interface ComposerOpts {
  replyTo?: string;
  placeholder?: string;
  /** preselect "post as" this studio (its page's composer) */
  studio?: string;
  /** preset game to embed (a game page's composer) */
  game?: string;
  onPosted: (p: Post) => void;
}

/** The studios you've claimed (accounts), for "post as". */
export async function myStudios(userId: string): Promise<{ id: string; name: string }[]> {
  try {
    return (await api<{ creators: { id: string; name: string }[] }>("accounts", `/users/${encodeURIComponent(userId)}/creators`, { auth: false })).creators ?? [];
  } catch {
    return [];
  }
}

/** Text plus an optional game to embed (any game on Banger, by its catalogue id or picked from search). */
export function composer(opts: ComposerOpts): HTMLElement {
  const text = h("textarea", { name: "body", rows: opts.replyTo ? 2 : 3, maxlength: 2000, placeholder: opts.placeholder ?? "What are you playing or building?", "aria-label": opts.replyTo ? "Your reply" : "Your banger" });
  const game = h("input", { name: "game", list: "people-games", placeholder: "Add a game (optional)", "aria-label": "Game to add", autocomplete: "off" });
  const options = h("datalist", { id: "people-games" });
  const msg = h("div", { "aria-live": "polite" });
  const titles = new Map<string, string>();
  let presetTitle = "";
  void getCatalogue()
    .then((cat) => {
      for (const g of cat.games.slice(0, 400)) {
        titles.set(g.title.toLowerCase(), g.id);
        options.append(h("option", { value: g.title }));
      }
      const g = opts.game ? gameById(cat, opts.game) : null;
      if (g) {
        titles.set(g.title.toLowerCase(), g.id);
        presetTitle = g.title;
        (game as HTMLInputElement).value = g.title;
      }
    })
    .catch(() => {});
  const send = h("button", { class: "btn", type: "submit" }, opts.replyTo ? "Reply" : "Post a banger");
  // claimers can post as their studio (BANGERS.md slice 1); hidden when they have none
  const as = h("select", { name: "as", "aria-label": "Post as", class: "composer-as", hidden: true }) as HTMLSelectElement;
  const me = currentMe();
  if (me && !opts.replyTo)
    void myStudios(me.id).then((list) => {
      if (!list.length) return;
      clear(as, h("option", { value: "" }, `As ${me.displayName ?? (me.handle ? `@${me.handle}` : "you")}`), list.map((c) => h("option", { value: c.id, selected: c.id === opts.studio }, `As ${c.name}`)));
      as.hidden = false;
    });
  return h(
    "form",
    {
      class: "composer",
      onsubmit: async (e: Event) => {
        e.preventDefault();
        const body = (text as HTMLTextAreaElement).value.trim();
        if (!body) return;
        const picked = (game as HTMLInputElement).value.trim();
        const gid = picked ? (titles.get(picked.toLowerCase()) ?? (/^g_[a-z0-9-]+$/.test(picked) ? picked : null)) : null;
        if (picked && !gid) {
          clear(msg, h("p", { class: "error small", role: "alert" }, "Pick a game from the list, or leave it empty."));
          return;
        }
        (send as HTMLButtonElement).disabled = true;
        try {
          const studio = as.hidden ? "" : as.value;
          const r = await api<{ post: Post }>("social", "/posts", { body: { body, ...(gid ? { embed: { kind: "game", id: gid } } : {}), ...(opts.replyTo ? { reply_to: opts.replyTo } : {}), ...(studio ? { as_studio: studio } : {}) } });
          (text as HTMLTextAreaElement).value = "";
          (game as HTMLInputElement).value = presetTitle;
          clear(msg);
          opts.onPosted(r.post);
        } catch (x) {
          clear(msg, h("p", { class: "error small", role: "alert" }, peopleError(x)));
        } finally {
          (send as HTMLButtonElement).disabled = false;
        }
      },
    },
    avatar(currentMe() ? (currentMe()!.displayName ?? currentMe()!.handle) : null, "", currentMe()?.avatarPortrait ?? null),
    h("div", { class: "composer-main" }, text, h("div", { class: "composer-row" }, opts.replyTo ? null : game, options, as, h("span", { class: "topbar-fill" }), send), msg),
  );
}

// ---- a studio's and a game's bangers (BANGERS.md slice 1) ----

/** "Bangers" on a studio page (/c/:id): what its developer posted as it. Hidden when empty, except for the claimer, who gets the composer. */
export function studioBangers(studioId: string, claimedBy: string | null): HTMLElement {
  peopleStyles();
  const el = h("section", { class: "cr-section people-embed", hidden: true });
  const list = h("div", { class: "posts" });
  const mine = !!claimedBy && currentMe()?.id === claimedBy;
  const authed = signedIn();
  void api<{ posts: Post[] }>("social", authed ? `/creators/${encodeURIComponent(studioId)}/posts?limit=10` : `/public/creators/${encodeURIComponent(studioId)}/posts?limit=10`, { auth: authed })
    .then((r) => {
      if (!r.posts.length && !mine) return el.remove();
      clear(list, r.posts.map((p) => postCard(p, { permalink: true, onDelete: () => list.querySelector(`[data-post="${p.id}"]`)?.remove() })));
      clear(el, h("h2", { class: "cr-h2" }, "Bangers"), mine ? composer({ studio: studioId, placeholder: "Tell your players what's new", onPosted: (p) => list.prepend(postCard(p)) }) : null, list);
      el.hidden = false;
    })
    .catch(() => el.remove());
  return el;
}

/** "Bangers" on a game page: the developer's (posted as the game's studio) first, then everyone's. Hidden when empty. */
export function gameBangers(gameId: string, studioId: string | null): HTMLElement {
  peopleStyles();
  const el = h("section", { class: "people-embed game-bangers", hidden: true });
  const authed = signedIn();
  const qs = new URLSearchParams({ limit: "6", ...(studioId ? { studio: studioId } : {}) });
  void api<{ developer: Post[]; posts: Post[] }>("social", `${authed ? "" : "/public"}/games/${encodeURIComponent(gameId)}/posts?${qs}`, { auth: authed })
    .then((r) => {
      if (!r.developer.length && !r.posts.length) return el.remove();
      clear(
        el,
        r.developer.length ? [h("h2", null, "From the developer"), h("div", { class: "posts" }, r.developer.slice(0, 3).map((p) => postCard(p)))] : null,
        r.posts.length ? [h("h2", null, "Bangers about it"), h("div", { class: "posts" }, r.posts.map((p) => postCard(p)))] : null,
        authed ? h("p", { class: "small" }, link("/feed", null, "Post a banger about it")) : null,
      );
      el.hidden = false;
    })
    .catch(() => el.remove());
  return el;
}

// ---- playing now ----

/** Friends in a game, with Join when their party is open. Join asks social for the game, then opens the app. */
export function playingStrip(list: Playing[]): HTMLElement | null {
  if (!list.length) return null;
  const titleOf = new Map<string, string>();
  const row = h("ul", { class: "playing" });
  const draw = () =>
    clear(
      row,
      list.map((f) =>
        h(
          "li",
          { class: "playing-item" },
          avatar(f.name, "small"),
          h("div", { class: "playing-who" }, h("strong", null, f.name ?? "A friend"), h("span", { class: "dim small" }, `Playing ${titleOf.get(f.game_id) ?? "a game"}`)),
          f.joinable
            ? h(
                "button",
                {
                  type: "button",
                  class: "btn small",
                  onclick: async () => {
                    try {
                      const r = await api<{ game_id: string | null }>("social", `/friends/${f.user_id}/join`, { method: "POST" });
                      const g = r.game_id ?? f.game_id;
                      launch(g, { onMissing: () => navigate(downloadPath(g)) });
                    } catch (e) {
                      toast(peopleError(e));
                    }
                  },
                },
                "Join",
              )
            : null,
        ),
      ),
    );
  draw();
  void getCatalogue()
    .then((cat) => {
      for (const f of list) {
        const g = gameById(cat, f.game_id);
        if (g) titleOf.set(f.game_id, g.title);
      }
      draw();
    })
    .catch(() => {});
  return h("section", { class: "playing-now", "aria-label": "Friends playing now" }, h("h2", null, "Playing now"), row);
}

/**
 * "From people you follow": the newest posts from the Following tab as a compact row, for Home (banger-zone-home
 * places it). Signed out or with nothing to show, it renders nothing.
 */
export function followingRow(limit = 8): HTMLElement {
  peopleStyles();
  const host = h("section", { class: "following-row", hidden: true });
  if (!signedIn()) return host;
  void api<FeedPage>("social", `/feed?tab=following&limit=${limit}`)
    .then((page) => {
      const me = currentMe();
      const posts = page.posts.filter((p) => p.author.id !== me?.id);
      if (!posts.length && !page.playing.length) return;
      host.hidden = false;
      clear(
        host,
        h("div", { class: "row-head" }, h("h2", null, "From people you follow"), link("/feed", { class: "more" }, "Your feed")),
        playingStrip(page.playing),
        h("ul", { class: "mini-posts" }, posts.map((p) => h("li", null, link(`/p/${p.id}`, { class: "mini-post" }, h("strong", null, p.author.name ?? "Player"), h("span", { class: "mini-body" }, p.body))))),
      );
    })
    .catch(() => {});
  return host;
}
